import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu, Network, MessageSquare, Shield, Vote, Crown, Briefcase, Trophy, MessageCircle, Inbox, Banknote } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSidebar } from "@/contexts/SidebarContext";

interface NavbarSidebarProps {
  isAdmin: boolean;
}

export default function NavbarSidebar({ isAdmin }: NavbarSidebarProps) {
  const { isOpen, toggleSidebar, registerTriggerElement, registerSidebarElement } = useSidebar();

  return (
    <>
      {/* Hamburger Menu Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="outline" 
            size="icon" 
            className="font-inter"
            onClick={toggleSidebar}
            ref={(el) => registerTriggerElement(el as unknown as HTMLElement | null)}
          >
            <Menu size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Menu</p>
        </TooltipContent>
      </Tooltip>

      {/* Persistent Sidebar - positioned below navbar */}
      <div
        ref={(el) => registerSidebarElement(el as unknown as HTMLElement | null)}
        className={`
        fixed top-[73px] right-0 h-[calc(100vh-73px)] w-20 bg-background border-l border-border shadow-2xl z-40
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'}
        flex flex-col items-center py-4
      `}>
        {/* Top section with main navigation */}
        <div className="flex flex-col items-center space-y-4">

          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/admin-dashboard">
                  <Button variant="outline" size="icon" className="font-inter">
                    <Shield size={16} />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Admin Dashboard</p>
              </TooltipContent>
            </Tooltip>
          )}

          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/admin/emails">
                  <Button variant="outline" size="icon" className="font-inter">
                    <Inbox size={16} />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Admin Emails</p>
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/conversations">
                <Button variant="outline" size="icon" className="font-inter">
                  <MessageCircle size={16} />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Conversations</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/chat-rooms">
                <Button variant="outline" size="icon" className="font-inter">
                  <Network size={16} />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Chat Rooms</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/my-services">
                <Button variant="outline" size="icon" className="font-inter">
                  <Briefcase size={16} />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>My Services and Requests</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/progress">
                <Button variant="outline" size="icon" className="font-inter">
                  <Trophy size={16} />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Progress & Quests</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/transactions">
                <Button variant="outline" size="icon" className="font-inter">
                  <Banknote size={16} />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Payments & Payouts</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Bottom section with Polls and Feedback */}
        {!isAdmin && (
          <div className="mt-auto pb-2 flex flex-col items-center space-y-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/polls">
                  <Button variant="outline" size="icon" className="font-inter">
                    <Vote size={16} />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Polls</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/feedback">
                  <Button variant="outline" size="icon" className="font-inter">
                    <MessageSquare size={16} />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Feedback</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </>
  );
}
